import React, { useCallback, useMemo } from 'react';
import { Checkbox } from '../../components/Checkbox';
import { DragAndDrop } from '../../components/DragAndDrop';
import { Icon } from '../../components/Icon';
import { Text } from '../../components/Text';
import { Box } from '../Box';
import { OptionType } from '../SelectMenu';

export type NestedDragAndDropMenuProps<T> = {
  items: OptionType<T>[];
  selected: OptionType<T>[];
  onChange: (newItems: OptionType<T>[], optionalLog?: string) => void;
  onReorder: (keys: string[]) => void;
  width?: number;
  Muuri: React.ComponentProps<typeof DragAndDrop>['Muuri'];
};

export function NestedDragAndDropMenu<T>({
  items,
  selected,
  onChange,
  onReorder,
  width = 240,
  Muuri,
}: NestedDragAndDropMenuProps<T>) {
  const itemToIndex = useMemo(() => new Map(items.map((item, i) => [item.key, i])), [items]);

  const handleDragEnd = useCallback(
    (_: OptionType<T>, reorderedItems: OptionType<T>[]) => {
      onReorder(reorderedItems.map(i => i.key));
    },
    [onReorder],
  );

  const onToggle = useCallback(
    (item: OptionType<T>, checked: boolean) => {
      const newItems = [...selected];
      if (checked) {
        newItems.push(item);
      } else {
        const index = newItems.findIndex(i => i.key === item.key);
        newItems.splice(index, 1);
      }
      onChange(newItems, 'onToggle');
    },
    [selected, onChange],
  );

  // force render Muuri when items change to pick up array changes
  const renderKey = useMemo(() => items.map(i => i.key).join(','), [items]);

  return (
    <div data-component="NestedDragAndDropMenu" style={{ width }}>
      <DragAndDrop<OptionType<T>>
        gridName="indented-grid"
        items={items}
        onDragEnd={handleDragEnd}
        data-component="NestedDragAndDropMenu"
        key={renderKey}
        Muuri={Muuri}
      >
        {item => (
          <DragAndDrop.Item>
            <div
              data-component="NestedDragAndDropMenu-Item"
              data-id={item.label}
              style={{ paddingLeft: '4px' }}
            >
              <Box
                width={width}
                display="flex"
                alignItems="center"
                style={{
                  paddingLeft: `${(itemToIndex.get(item.key) || 0) * 16}px`,
                }}
              >
                <Box
                  display="flex"
                  flex={1}
                  alignItems="center"
                  justifyContent="space-between"
                  maxWidth={width - (itemToIndex.get(item.key) || 0) * 16 - 24}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={4}
                    maxWidth={width - (itemToIndex.get(item.key) || 0) * 16 - 48}
                  >
                    <Checkbox
                      checked={!!selected.find(maybeOption => maybeOption.key === item.key)}
                      onChange={checked => onToggle(item, checked)}
                    />
                    <Text noWrap appearance="bodyMedium">
                      {item.label}
                    </Text>
                  </Box>
                  <Icon name="drag" style={{ cursor: 'grab', marginRight: 8 }} />
                </Box>
              </Box>
            </div>
          </DragAndDrop.Item>
        )}
      </DragAndDrop>
    </div>
  );
}
